import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BackendResponse, CurrencyAmount, Item } from '@qtek/shared/models';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface AccountLookupResponse {
  id?: string;
  name?: string;
  domains?: string[];
}

interface EmailValidationResponse {
  status?: string;
  existingExactMatch?: boolean;
  existingEmailMatches?: string[];
  correctedEmail?: string;
}

/**
 * Body of `POST /api/v2/deals/external-invest`
 * (`io.qtek.http.server.service.api.ExternalDealUploadRequest`). The caller records its own
 * participation in a deal led by `organizationId`; the backend takes the investing company from
 * the session, never from here.
 */
export interface ExternalDealUploadRequest {
  /** The lead bank that offers the deal. */
  organizationId?: string;
  /** The private-copy deal to create. */
  asset: Pick<Item, 'name' | 'itemSide' | 'itemData' | 'templateStructureId'>;
  /** Optional lead contact. Its domain must belong to `organizationId`. */
  email?: string;
  /** The amount the caller indicates. */
  indicatedValue: CurrencyAmount;
}

/** Ids created by a successful external deal upload. */
export interface ExternalDealUploadResponse {
  itemId?: string;
  participationId?: string;
}

@Injectable({ providedIn: 'root' })
export class LeadBankService {
  private http = inject(HttpClient);

  getLeadBanks(): Observable<any[]> {
    return this.http
      .get<{ res?: AccountLookupResponse[] }>('/api/v2/company/lookup', {
        withCredentials: true,
      })
      .pipe(map(resp => this.mapLeadBankOptions(resp?.res ?? [])));
  }

  validateEmail(email: string): Observable<{
    sts: string;
    correctedEmail?: string;
    existingEmailMatches?: string[];
    message?: string;
  }> {
    return this.http
      .post<{
        res?: EmailValidationResponse;
        sts?: { attrs?: Record<string, any> };
      }>('/api/v2/user/email/validate', { email }, { withCredentials: true })
      .pipe(
        map(resp => {
          const body = resp?.res;

          return {
            sts: body?.status ?? '',
            correctedEmail: body?.correctedEmail,
            existingEmailMatches: body?.existingEmailMatches,
            message: this.messageFromAttrs(resp?.sts?.attrs),
          };
        }),
        catchError(error =>
          of({
            sts: 'err',
            message:
              this.messageFromAttrs(error?.error?.sts?.attrs) ??
              'FTR_CUSTOMER_INVITE_INVALID_EMAIL',
          })
        )
      );
  }

  private messageFromAttrs(attrs: unknown): string | undefined {
    const errors = Object.keys(attrs ?? {}).map(key => ({
      key,
      ...(attrs as any)[key],
    }));
    const main = errors.find(err => err.main) || errors[0];

    return main ? main.msg || main.key : undefined;
  }

  submitExternalInvest(
    payload: ExternalDealUploadRequest
  ): Observable<BackendResponse<ExternalDealUploadResponse>> {
    const headers = new HttpHeaders().append(
      'X-Skip-Revalidate-Error-Handling',
      ''
    );

    return this.http.post<BackendResponse<ExternalDealUploadResponse>>(
      '/api/v2/deals/external-invest',
      payload,
      { withCredentials: true, headers }
    );
  }

  private mapLeadBankOptions(
    data: AccountLookupResponse[] | null | undefined
  ): any[] {
    if (!data || !Array.isArray(data)) return [];
    const unique = new Map<string, any>();

    data.forEach(entry => {
      const name = entry?.name || '';
      const companyId = entry?.id;
      const domains = Array.isArray(entry?.domains) ? entry.domains : [];
      const key = companyId || name;

      if (!key) return;
      if (!unique.has(key)) {
        unique.set(key, {
          companyId,
          name,
          email: '',
          domains,
          raw: entry,
        });
      }
    });

    return Array.from(unique.values());
  }
}
